import { useSearchParams } from 'react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ProductDetails } from './sections/ProductDetails';
import { PricesAndAvailability } from './sections/PricesAndAvailability';
import {
  useGetSellerProductInventoriesForBuyer,
  useGetSellerVendorInventoryGroupsForBuyer,
  useGetBuyerInventoryByProductAndLocation,
  useGetPlatformProductsLengthsForProduct,
} from '~/api/inventory';
import {
  defaultCannedTallyVehicleId,
  useCannedTallySellerGroupsFetch,
} from '~/api/canned-tally';
import { useComparisonSavedViews } from '~/pages/order-builder/lib/useComparisonSavedViews';
import {
  buildComparisonFiltersInput,
  comparisonViewChanged,
} from '~/pages/order-builder/lib/comparisonFilters';
import { useDebounce } from '~/hooks/useDebounce';
import type {
  Cart,
  Inventory,
  Money,
  PlatformProduct,
  PlatformProductLength,
  SellerComparisonFiltersInput,
  CannedTallySellerGroupsForBuyerQuery,
  VehicleType,
} from '~/graphql/graphql';
import { useGetCart, useGetOrderById } from '~/api';
import type { OrderForBuilder } from '~/api/order';
import {
  useActiveSourcingItem,
  type ActiveSourcingItem,
} from '~/pages/order-builder/useActiveSourcingItem';
import InventoryRequirement from '~/pages/order-builder/sections/InventoryRequirement';
import { SourcingOrderItems } from '~/pages/order-builder/sections/SourcingOrderItems';
import { SourcingCoverageGuide } from '~/pages/order-builder/sections/SourcingCoverageGuide';
import { mergePriceChangesAcrossRefresh } from '~/pages/order-builder/priceEdit';
import { formatMarketMidPrice } from '~/utils/marketMidPrice';
import { buildComparisonInventoryFilters } from '~/pages/order-builder/comparisonInventoryFilters';
import {
  CANNED_TALLY_SORTABLE_KEYS,
  VENDOR_GROUP_SORTABLE_KEYS,
  toComparisonSort,
} from '~/pages/order-builder/comparisonSort';
import { usePaginatedQuery, type RouteSort } from '~/hooks/usePaginatedQuery';
import {
  cartsBelongingToParentOrder,
  regularCarts,
  computeSourcingCoverage,
  remainingInUnit,
} from '~/pages/order-builder/sourcingCoverage';
import {
  isUnitChangeOnSameProduct,
  type SettledUnit,
} from '~/pages/order-builder/settledUnit';
import { OrderCalculator } from './sections/OrderCalculator';
import { useDispatch, useSelector } from 'react-redux';
import {
  discardEditedVendorGroupPricesNotInUnit,
  setInventory,
  setSellerInventoriesForBuyer,
} from '~/redux/reducers/inventory';
import { setCarts, setSelectedLocationId } from '~/redux/reducers/cart';
import { useDeepLinkedLocationId } from '~/hooks/useDeepLinkedLocationId';
import { BackButton } from '~/components/ui';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { useTranslation } from 'react-i18next';
import { useGetPlatformProductById } from '~/api/platformProducts';
import type { RootState } from '~/redux/store';
import { roundTo6dp } from '~/utils';
import { roundUnitPrice } from '~/utils/unitPrice';
import { usePreferredUnit } from '~/hooks/usePreferredUnit';
import { useGetUserPreferences } from '~/api/auth';
import {
  getPlatformProductDimensions,
  roundUpToBundleQty,
} from '~/utils/order';
import {
  vehicleTypeToTransportMode,
  type TransportMode,
} from '~/hooks/useTransportState';
import {
  calculateTargetOrderSize,
  quantityInUnit,
} from '~/utils/targetOrderSize';
import { cartQuantityForProductLength } from '~/utils/cartQuantity';
import { useGetTransportVehicles } from '~/api/facility';
import { useQueryClient } from '@tanstack/react-query';
import {
  inventoryOnlyLengthIds,
  knownLengthIds,
  initialLengthSelection,
  productIdForQuery,
  resolveEffectiveLengthIds,
  revealsInventoryLengths,
  shouldFallBackToLengths,
  singleSelectedLengthId,
  withoutInventoryOnlyLengths,
  type LengthMode,
} from '~/pages/order-builder/sections/lengthSelection';

function weeksToCover(
  orderSize: number,
  usageRatePerWeek: number | null
): number | null {
  return usageRatePerWeek
    ? parseFloat((orderSize / usageRatePerWeek).toFixed(2))
    : null;
}

function withTargetOrderSize(
  prev: OrderInfo,
  targetOrderSize: number,
  usageRatePerWeek: number | null
): OrderInfo {
  return {
    ...prev,
    targetOrderSize,
    targetWeeksToCover:
      weeksToCover(targetOrderSize, usageRatePerWeek) ??
      prev.targetWeeksToCover,
  };
}

function vehicleLabelFor(
  vehicle: { vehicleType: VehicleType } | undefined
): TransportMode {
  return vehicle ? vehicleTypeToTransportMode(vehicle.vehicleType) : 'truck';
}

function sourcingDestinationLabel(order: OrderForBuilder): string | undefined {
  const shippingLocation = order.shippingLocation;
  if (!shippingLocation) return undefined;
  return [
    shippingLocation.organization.name,
    shippingLocation.facility.address.city,
  ]
    .filter(Boolean)
    .join(', ');
}

export type OrderInfo = {
  targetPrice: number;
  targetOrderSize: number | null;
  targetWeeksToCover: number;
};

/**
 * Present only when the builder was reached from a parent order's Sourcing panel
 * (URL has `parentOrderId`). Drives the order-items selector, routes cart upserts to
 * sub-orders of `order`, and swaps the sidebar's inventory card for a coverage guide.
 */
type SourcingContext = {
  parentOrderId: string;
  dropShipLocationId: string | null;
  order: OrderForBuilder;
  activeLengthId: string;
  onSelectItem: (item: ActiveSourcingItem | null) => void;
};

type OrderBuilderProps = {
  platformProductId: string;
  platformProductLengthId: string | null;
  returnPath: string;
  sourcing?: SourcingContext;
};

const COMPARISON_FILTER_QUERY_DEBOUNCE_MS = 400;

type SellerInventoriesRawResult = {
  sellerProductInventoriesForBuyer?: {
    paging?: { number?: number; totalElements?: number };
    recommendedPrice?: Pick<Money, 'amount' | 'currency'> | null;
    recommendedPriceCount?: number;
    sellersCount?: number;
  };
  cannedTallySellerGroupsForBuyer?: {
    paging?: { totalElements?: number };
  };
};

function OrderBuilderPage({
  platformProductId,
  platformProductLengthId,
  returnPath,
  sourcing,
}: Readonly<OrderBuilderProps>) {
  const { t } = useTranslation('order-builder');
  const queryClient = useQueryClient();
  const initialSelection = initialLengthSelection(platformProductLengthId);
  const [selectedLengthIds, setSelectedLengthIds] = useState<string[]>(
    initialSelection.selectedLengthIds
  );
  const [mode, setMode] = useState<LengthMode>(initialSelection.mode);
  const [orderInfo, setOrderInfo] = useState<OrderInfo>({
    targetPrice: 0,
    targetOrderSize: 0,
    targetWeeksToCover: 0,
  });
  // True once the user manually edits targetOrderSize or targetWeeksToCover.
  // Prevents the inventory-defaulting effect from overwriting user input on
  // cart changes (e.g. after adding an item the effect re-runs to update cartQty,
  // but we must not silently reset a value the user already typed).
  // Reset whenever the product/length context changes.
  const targetOrderSizeEditedRef = useRef(false);
  const revealToggleProductIdRef = useRef(platformProductId);

  const locationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );

  // A Compare deep link carries the location of the inventory row clicked, but
  // Redux only catches up once InventoryRequirement has seeded it. Latched, so a
  // location the buyer picks afterwards doesn't disable the query again.
  const deepLinkedLocationId = useDeepLinkedLocationId();
  const hasSeededDeepLinkedLocation = useRef(false);
  if (
    deepLinkedLocationId !== undefined &&
    (!deepLinkedLocationId || locationId === deepLinkedLocationId)
  ) {
    hasSeededDeepLinkedLocation.current = true;
  }

  const dispatch = useDispatch();

  // In sourcing mode there's no location selector (unlike the buyer flow, which defaults
  // it from InventoryRequirement): the drop-ship location is fixed by the parent order.
  useEffect(() => {
    if (sourcing?.dropShipLocationId) {
      dispatch(setSelectedLocationId(sourcing.dropShipLocationId));
    }
  }, [sourcing?.dropShipLocationId, dispatch]);

  const effectiveLengthIds = useMemo(
    () => resolveEffectiveLengthIds(selectedLengthIds, mode),
    [selectedLengthIds, mode]
  );

  const singleLengthId = singleSelectedLengthId(effectiveLengthIds);

  const platformProductIdForQuery = productIdForQuery(
    effectiveLengthIds,
    platformProductId
  );

  const [sellerInventorySort, setSellerInventorySort] = useState<
    RouteSort | undefined
  >();

  const [sellerVendorGroupSort, setSellerVendorGroupSort] = useState<
    RouteSort | undefined
  >();

  const handleSellerVendorGroupSort = (
    field: string,
    direction: 'asc' | 'desc' | null
  ) => {
    setSellerVendorGroupSort(
      toComparisonSort(VENDOR_GROUP_SORTABLE_KEYS, field, direction)
    );
  };

  const handleSellerInventorySort = (
    field: string,
    direction: 'asc' | 'desc' | null
  ) => {
    setSellerInventorySort(
      toComparisonSort(CANNED_TALLY_SORTABLE_KEYS, field, direction)
    );
  };

  const { data: transportVehiclesData, isLoading: isTransportVehiclesLoading } =
    useGetTransportVehicles();
  const { data: platformProductLengths, isLoading: isLengthsLoading } =
    useGetPlatformProductsLengthsForProduct(platformProductId);

  const defaultTransportVehicleId = defaultCannedTallyVehicleId(
    transportVehiclesData?.transportVehicles
  );
  const railcarVehicleId =
    transportVehiclesData?.transportVehicles?.find(
      (v) => v.name === "73' CENTERBEAM"
    )?.id ?? null;

  const [transportMode, setTransportMode] = useState<'trucks' | 'railcars'>(
    'trucks'
  );
  const [selectedTallyName, setSelectedTallyName] = useState<
    string | undefined
  >();
  const [includeInventoryLengths, setIncludeInventoryLengths] = useState(false);

  const productLengths = useMemo(
    () => platformProductLengths?.platformProductsLengthsForProduct ?? [],
    [platformProductLengths]
  );

  const inventoryOnlyIds = useMemo(
    () => inventoryOnlyLengthIds(productLengths),
    [productLengths]
  );

  const revealInventoryLengths = revealsInventoryLengths(
    includeInventoryLengths,
    selectedLengthIds,
    inventoryOnlyIds
  );

  const handleIncludeInventoryLengthsChange = (checked: boolean) => {
    setIncludeInventoryLengths(checked);
    if (!checked) {
      setSelectedLengthIds((ids) =>
        withoutInventoryOnlyLengths(ids, inventoryOnlyIds)
      );
    }
  };

  const currentTransportVehicleId =
    transportMode === 'railcars' ? railcarVehicleId : defaultTransportVehicleId;
  const currentTransportVehicle =
    transportVehiclesData?.transportVehicles?.find(
      (v) => v.id === currentTransportVehicleId
    );
  const vehicleLabel = vehicleLabelFor(currentTransportVehicle);

  const comparisonViews = useComparisonSavedViews();
  const [comparisonFiltersInput, setComparisonFiltersInput] =
    useState<SellerComparisonFiltersInput>(() =>
      buildComparisonFiltersInput(comparisonViews.rows)
    );
  const previousActiveViewIdRef = useRef(comparisonViews.activeViewId);
  const wasLoadingComparisonViewsRef = useRef(comparisonViews.isLoadingViews);
  const debouncedSetComparisonFiltersInput = useDebounce(
    (rows: typeof comparisonViews.rows) =>
      setComparisonFiltersInput(buildComparisonFiltersInput(rows)),
    COMPARISON_FILTER_QUERY_DEBOUNCE_MS
  );
  useEffect(() => {
    const justHydrated =
      wasLoadingComparisonViewsRef.current && !comparisonViews.isLoadingViews;
    wasLoadingComparisonViewsRef.current = comparisonViews.isLoadingViews;
    const activeViewChanged = comparisonViewChanged(
      previousActiveViewIdRef.current,
      comparisonViews.activeViewId
    );
    previousActiveViewIdRef.current = comparisonViews.activeViewId;
    if (justHydrated || activeViewChanged) {
      debouncedSetComparisonFiltersInput.cancel();
      setComparisonFiltersInput(
        buildComparisonFiltersInput(comparisonViews.rows)
      );
    } else {
      debouncedSetComparisonFiltersInput(comparisonViews.rows);
    }
  }, [
    comparisonViews.rows,
    comparisonViews.activeViewId,
    comparisonViews.isLoadingViews,
    debouncedSetComparisonFiltersInput,
  ]);

  const sharedInventoryFilters = useMemo(
    () =>
      buildComparisonInventoryFilters({
        platformProductLengthIds: effectiveLengthIds,
        platformProductId: platformProductIdForQuery,
        destinationLocationId: locationId,
        includeInventoryLengths: revealInventoryLengths,
        comparisonFilters: comparisonFiltersInput,
      }),
    [
      effectiveLengthIds,
      platformProductIdForQuery,
      locationId,
      revealInventoryLengths,
      comparisonFiltersInput,
    ]
  );

  const isCannedTally = mode === 'cannedTally';

  // The Canned Tally tab is disabled without a transport vehicle, so if the vehicle goes away
  // while that mode is active — switching to railcars on a product with no railcar vehicle —
  // fall back to lengths. Otherwise the tab sits disabled-but-selected and the tally query
  // still runs with no vehicle to group by.
  useEffect(() => {
    if (
      shouldFallBackToLengths(
        mode,
        currentTransportVehicleId,
        isTransportVehiclesLoading
      )
    ) {
      setMode('lengths');
    }
  }, [mode, currentTransportVehicleId, isTransportVehiclesLoading]);

  // Gated via `enabled`, not by branching the usePaginatedQuery call itself, so toggling
  // isCannedTally never changes hook call order (react-hooks/rules-of-hooks).
  const cannedTallySellerInventories = usePaginatedQuery({
    fetchHook: useCannedTallySellerGroupsFetch,
    select: (data) => data?.cannedTallySellerGroupsForBuyer,
    options: {
      pageSize: 20,
      enabled: isCannedTally && !comparisonViews.isLoadingViews,
      filters: {
        platformProductId,
        transportVehicleId: currentTransportVehicleId ?? undefined,
        tallyName: selectedTallyName,
        destinationLocationId: locationId,
        deliveryMode: sharedInventoryFilters.deliveryMode,
        comparisonFilters: comparisonFiltersInput,
      },
      sort: sellerInventorySort,
      catchUpOnSortChange: true,
    },
  });
  const { isLoading: isCannedTallySellerInventoriesLoading } =
    cannedTallySellerInventories;
  const fixedLengthSellerInventories = usePaginatedQuery({
    fetchHook: useGetSellerProductInventoriesForBuyer,
    select: (data) => data?.sellerProductInventoriesForBuyer,
    options: {
      pageSize: 20,
      enabled: !isCannedTally && !comparisonViews.isLoadingViews,
      eager: true,
      filters: sharedInventoryFilters,
    },
  });
  const {
    data: sellerProductInventoriesAccumulated,
    hasMoreData: hasMoreSellerInventories,
    isLoadingMore: isLoadingMoreSellerInventories,
    sentinelRef: sellerInventoriesSentinelRef,
    raw: sellerProductInventoriesRaw,
    reset: resetSellerInventories,
  } = isCannedTally
    ? cannedTallySellerInventories
    : fixedLengthSellerInventories;

  const cannedTallyGroups = isCannedTally
    ? (sellerProductInventoriesAccumulated as CannedTallySellerGroupsForBuyerQuery['cannedTallySellerGroupsForBuyer']['content'])
    : undefined;

  const {
    data: sellerVendorGroupsAccumulated,
    isLoading: isSellerVendorGroupsLoading,
    isLoadingMore: isSellerVendorGroupsLoadingMore,
    hasMoreData: hasMoreSellerVendorGroups,
    sentinelRef: sellerVendorGroupsSentinelRef,
    reset: resetSellerVendorGroups,
  } = usePaginatedQuery({
    fetchHook: useGetSellerVendorInventoryGroupsForBuyer,
    select: (data) => data?.sellerVendorInventoryGroupsForBuyer,
    options: {
      pageSize: 20,
      enabled: !isCannedTally && !comparisonViews.isLoadingViews,
      filters: sharedInventoryFilters,
      sort: sellerVendorGroupSort,
      catchUpOnSortChange: true,
    },
  });

  const sellerInventories = useSelector(
    (state: RootState) => state.inventory.sellerInventoriesForBuyer
  );

  const { data: cartByLocation } = useGetCart(locationId, {
    enabled: hasSeededDeepLinkedLocation.current,
  });

  // In sourcing mode, scope the location's cart list down to sub-order carts belonging
  // to this parent order, so an unrelated cart at the same drop-ship location doesn't
  // bleed into cart totals, coverage, or "already in cart" seller-row detection.
  const carts = useMemo(() => {
    const allCarts = (cartByLocation?.carts as Cart[]) || [];
    return sourcing
      ? cartsBelongingToParentOrder(allCarts, sourcing.parentOrderId)
      : regularCarts(allCarts);
  }, [cartByLocation?.carts, sourcing]);

  const actualLengthId =
    productLengths.find((ppl) => ppl.id === singleLengthId)?.length.id ?? '';

  const { data: inventory, isFetching: isInventoryFetching } =
    useGetBuyerInventoryByProductAndLocation(
      actualLengthId,
      platformProductId,
      locationId
    );

  const { data: platformProductData, isLoading: isProductLoading } =
    useGetPlatformProductById(platformProductId);

  // Seller/vendor-group loading also covers the placeholder window on a length/shipping-address
  // filter change (see usePaginatedQuery), which would otherwise unmount the whole page — and
  // its own child state along with it — on every such change. The table renders its own scoped
  // spinner for that instead.
  const isPageLoading = isProductLoading || isLengthsLoading;
  const product = platformProductData?.platformProduct as PlatformProduct;

  const preferredUnit = usePreferredUnit(
    product?.category ?? null,
    product?.standardUnitsCode ?? 'MBF'
  );

  const { isSuccess: arePreferencesLoaded } = useGetUserPreferences();
  const settledUnitRef = useRef<SettledUnit | null>(null);
  useEffect(() => {
    if (!product || !arePreferencesLoaded) return;
    const next = { productId: product.id, unit: preferredUnit };
    if (isUnitChangeOnSameProduct(settledUnitRef.current, next)) {
      dispatch(discardEditedVendorGroupPricesNotInUnit(preferredUnit));
    }
    settledUnitRef.current = next;
  }, [dispatch, product, arePreferencesLoaded, preferredUnit]);

  const selectedPpl = productLengths.find((ppl) => ppl.id === singleLengthId);

  const rawLatestPriceMid = selectedPpl?.latestPriceMid;
  const { widthInches, thicknessInches } = getPlatformProductDimensions(
    product,
    selectedPpl?.length,
    selectedPpl?.isNominal
  );
  const pplLengthInches = selectedPpl?.isNominal
    ? (selectedPpl?.length.nominalLengthInches ??
      selectedPpl?.length.actualLengthInches ??
      0)
    : (selectedPpl?.length.actualLengthInches ??
      product?.actualLengthInches ??
      0);
  const dims = useMemo(
    () => ({ lengthInches: pplLengthInches, widthInches, thicknessInches }),
    [pplLengthInches, widthInches, thicknessInches]
  );
  const rawAmount = rawLatestPriceMid?.amount;
  const rawCurrency = rawLatestPriceMid?.currency;
  const formattedMarketMidPrice = formatMarketMidPrice(
    rawAmount,
    rawCurrency,
    preferredUnit,
    product?.standardUnitsCode,
    dims
  );

  const usageRatePerWeekInPreferredUnit = quantityInUnit(
    inventory?.buyerInventoryByProductAndLocation?.buyerInventoryConfiguration
      ?.usageRatePerWeek,
    preferredUnit,
    dims
  );

  const handleOrderInfoChange = (name: string, value: string) => {
    const valueNumber = Number(value);

    if (name === 'targetWeeksToCover') {
      const targetOrderSize =
        usageRatePerWeekInPreferredUnit === null
          ? null
          : parseFloat(
              (usageRatePerWeekInPreferredUnit * valueNumber).toFixed(2)
            );
      setOrderInfo((prev) => ({
        ...prev,
        targetWeeksToCover: valueNumber,
        targetOrderSize,
      }));
      return;
    }

    if (name === 'targetOrderSize') {
      const targetWeeksToCover =
        weeksToCover(valueNumber, usageRatePerWeekInPreferredUnit) ?? 0;
      setOrderInfo((prev) => ({
        ...prev,
        targetOrderSize: valueNumber,
        targetWeeksToCover,
      }));
      return;
    }
    setOrderInfo((prev) => ({ ...prev, [name]: valueNumber }));
  };

  // recommendedPrice/recommendedPriceCount/sellersCount are the same for every page of a
  // given filter set, so the backend only computes them on page 0 and comes back empty/zero
  // on later pages instead of recomputing them. `raw` always reflects the most recently
  // fetched page, so without this the panel would blank out once the buyer scrolls past
  // page 0 — hold onto the page-0 values instead, and clear them when the filters change.
  const [firstPageAggregates, setFirstPageAggregates] = useState<{
    recommendedPrice: Pick<Money, 'amount' | 'currency'> | null;
    recommendedPriceCount: number;
    sellersCount: number;
  } | null>(null);

  useEffect(() => {
    setFirstPageAggregates(null);
  }, [effectiveLengthIds, platformProductIdForQuery, locationId]);

  useEffect(() => {
    if (isCannedTally) {
      setFirstPageAggregates(null);
      return;
    }
    const data = (sellerProductInventoriesRaw as SellerInventoriesRawResult)
      ?.sellerProductInventoriesForBuyer;
    if (data?.paging?.number === 0) {
      setFirstPageAggregates({
        recommendedPrice: data.recommendedPrice ?? null,
        recommendedPriceCount: data.recommendedPriceCount ?? 0,
        sellersCount: data.sellersCount ?? 0,
      });
    }
  }, [sellerProductInventoriesRaw, isCannedTally]);

  const recommendedPrice = firstPageAggregates?.recommendedPrice;
  const nCount = firstPageAggregates?.recommendedPriceCount ?? 0;
  const averageNPrice = recommendedPrice?.amount ?? null;

  // Invalidate user cache on mount so location IDs are always fresh before adding to cart.
  // Without this, a backend restart (which regenerates location IDs in the seeder) would
  // leave the Redux store with stale location IDs, causing "Location not found" errors.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['cur'] });
  }, [queryClient]);

  useEffect(() => {
    if (!platformProductLengthId) return;
    const next = initialLengthSelection(platformProductLengthId);
    setSelectedLengthIds((ids) =>
      ids.length === next.selectedLengthIds.length &&
      ids.every((id, index) => id === next.selectedLengthIds[index])
        ? ids
        : next.selectedLengthIds
    );
    // A deep link names one length, so it has to leave Canned Tally — that mode sends no
    // length at all and would silently drop the length the link was for.
    setMode(next.mode);
  }, [platformProductLengthId]);

  useEffect(() => {
    // Loading is also false when the query errored, and productLengths falls back to []; pruning
    // against that would silently clear a deep-linked selection with nothing to restore it.
    if (platformProductLengths === undefined) return;
    setSelectedLengthIds((ids) => {
      const known = knownLengthIds(ids, productLengths);
      return known.length === ids.length ? ids : known;
    });
  }, [productLengths, platformProductLengths]);

  useEffect(() => {
    if (revealToggleProductIdRef.current === platformProductId) return;
    revealToggleProductIdRef.current = platformProductId;
    setIncludeInventoryLengths(false);
    // Lengths belong to one product, and productIdForQuery drops the product id whenever ids are
    // present — so carrying a selection across a switch produces filters byte-identical to the
    // previous item's and React Query serves its rows under the new one.
    const next = initialLengthSelection(platformProductLengthId);
    setSelectedLengthIds(next.selectedLengthIds);
    setMode(next.mode);
  }, [platformProductId, platformProductLengthId]);

  // Reset the dirty flag whenever the product or length context changes so the
  // defaults are re-applied when the user navigates to a different product/length.
  useEffect(() => {
    targetOrderSizeEditedRef.current = false;
  }, [platformProductId, effectiveLengthIds]);

  useEffect(() => {
    if (isInventoryFetching) return;

    function setDefaultOrderInfo() {
      if (inventory?.buyerInventoryByProductAndLocation) {
        const usageRatePerWeek =
          inventory.buyerInventoryByProductAndLocation
            .buyerInventoryConfiguration?.usageRatePerWeek ?? null;
        const targetWeeksCovered =
          inventory.buyerInventoryByProductAndLocation
            .buyerInventoryConfiguration?.targetWeeksCovered || 0;
        const currentStock =
          inventory.buyerInventoryByProductAndLocation.quantity;

        const selectedLengthId = singleLengthId
          ? productLengths.find((ppl) => ppl.id === singleLengthId)?.length?.id
          : undefined;

        const cartQuantity = cartQuantityForProductLength(
          carts,
          selectedLengthId,
          platformProductId,
          dims
        );

        const suggestedTargetOrderSize = calculateTargetOrderSize({
          usageRatePerWeek,
          targetWeeksCovered,
          currentStock,
          onOrderStock:
            inventory.buyerInventoryByProductAndLocation.onOrderStock,
          inboundStock:
            inventory.buyerInventoryByProductAndLocation.inboundStock,
          cartQuantity,
          targetUnit: preferredUnit,
          dims,
        });
        const targetWeeksToCover =
          suggestedTargetOrderSize === null
            ? 0
            : (weeksToCover(
                suggestedTargetOrderSize,
                usageRatePerWeekInPreferredUnit
              ) ?? 0);
        setOrderInfo((prev) => ({
          ...prev,
          targetOrderSize: suggestedTargetOrderSize,
          targetWeeksToCover,
        }));
      } else {
        setOrderInfo((prev) => ({
          ...prev,
          targetOrderSize: 0,
          targetWeeksToCover: 0,
        }));
      }
    }
    // Sourcing mode has no buyer inventory config for the drop-ship location — its own
    // effect below defaults targetOrderSize from the parent line's remaining-to-allocate.
    if (!sourcing && !targetOrderSizeEditedRef.current) {
      setDefaultOrderInfo();
    }
    // buyerInventoryByProductAndLocation is gated on a single length id, so canned-tally mode
    // and multi-length selections have no inventory to show and must clear it rather than
    // write undefined into a slice typed Inventory | null.
    dispatch(
      setInventory(
        singleLengthId
          ? ((inventory?.buyerInventoryByProductAndLocation as Inventory) ??
              null)
          : null
      )
    );
  }, [
    dispatch,
    inventory?.buyerInventoryByProductAndLocation,
    isInventoryFetching,
    singleLengthId,
    productLengths,
    carts,
    platformProductId,
    sourcing,
    dims,
    preferredUnit,
    usageRatePerWeekInPreferredUnit,
  ]);

  // In sourcing mode, default the target order size to what's still needed on the active
  // parent line item (required minus already-covered), so "+ Add" on the seller table adds
  // toward the remaining allocation rather than a buyer-inventory-derived (and here absent)
  // target. Skipped once the trader edits the field manually.
  useEffect(() => {
    if (!sourcing) return;
    if (targetOrderSizeEditedRef.current) return;
    const activeRow = computeSourcingCoverage(sourcing.order, carts).find(
      (row) => row.platformProductsLengthId === sourcing.activeLengthId
    );
    if (!activeRow) return;
    const remaining = remainingInUnit(activeRow, preferredUnit, dims);
    if (remaining === null) return;

    setOrderInfo((prev) =>
      prev.targetOrderSize === remaining
        ? prev
        : { ...prev, targetOrderSize: remaining, targetWeeksToCover: 0 }
    );
  }, [sourcing, carts, preferredUnit, dims]);

  useEffect(() => {
    if (averageNPrice != null && averageNPrice > 0 && !rawAmount) {
      setOrderInfo((prev) => ({
        ...prev,
        targetPrice: roundUnitPrice(averageNPrice),
      }));
    }
  }, [averageNPrice, rawAmount]);

  useEffect(() => {
    dispatch(setCarts(carts));
  }, [dispatch, carts]);

  // Ref (not state) so a newly-loaded page can preserve each already-loaded row's
  // isPriceChanged flag without re-subscribing this effect to sellerInventories itself.
  const sellerInventoriesRef = useRef(sellerInventories);
  sellerInventoriesRef.current = sellerInventories;

  useEffect(() => {
    // CannedTallySellerGroup rows don't match the SellerInventoryForBuyer shape this slice expects.
    if (isCannedTally) {
      dispatch(setSellerInventoriesForBuyer([]));
      return;
    }
    const incoming = (sellerProductInventoriesAccumulated ?? []).filter(
      (inventory) => inventory != null
    );
    const sellerInventoriesWithPriceChange = mergePriceChangesAcrossRefresh(
      incoming,
      sellerInventoriesRef.current ?? []
    );
    dispatch(setSellerInventoriesForBuyer(sellerInventoriesWithPriceChange));
  }, [dispatch, sellerProductInventoriesAccumulated, isCannedTally]);

  const listingCount = isCannedTally
    ? ((sellerProductInventoriesRaw as SellerInventoriesRawResult)
        ?.cannedTallySellerGroupsForBuyer?.paging?.totalElements ?? 0)
    : ((sellerProductInventoriesRaw as SellerInventoriesRawResult)
        ?.sellerProductInventoriesForBuyer?.paging?.totalElements ?? 0);
  const sellersCount = firstPageAggregates?.sellersCount ?? 0;

  const handleTargetOrderSizeBlur = () => {
    const piecesPerBundle = product?.piecesPerBundle.value ?? 0;
    const { lengthInches, widthInches, thicknessInches } =
      getPlatformProductDimensions(
        product,
        selectedPpl?.length,
        selectedPpl?.isNominal
      );
    setOrderInfo((prev) => {
      if (prev.targetOrderSize === null) return prev;
      const rounded = roundUpToBundleQty(
        prev.targetOrderSize,
        piecesPerBundle,
        preferredUnit,
        lengthInches,
        widthInches,
        thicknessInches
      );
      if (rounded === prev.targetOrderSize) return prev;
      return withTargetOrderSize(
        prev,
        rounded,
        usageRatePerWeekInPreferredUnit
      );
    });
  };

  const handleSellerAdded = () => {
    resetSellerInventories();
    resetSellerVendorGroups();
  };

  // The target order size is what the next seller gets: a default target is recomputed net of
  // the cart, and a typed target is reduced by what was added.
  const handleAddedToCart = (quantity: number) => {
    if (!targetOrderSizeEditedRef.current) return;
    setOrderInfo((prev) =>
      prev.targetOrderSize === null
        ? prev
        : withTargetOrderSize(
            prev,
            roundTo6dp(Math.max(0, prev.targetOrderSize - quantity)),
            usageRatePerWeekInPreferredUnit
          )
    );
  };

  if (isPageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PageLoadingSpinner name={t('order_builder.loading')} />
      </div>
    );
  }

  if (!product || !platformProductLengths) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1>{t('order_builder.product_not_found')}</h1>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1">
            <div data-testid="back-button-container">
              <BackButton
                data-testid="back-button"
                link={returnPath}
                label=""
              />
            </div>

            <h1 className="text-xl sm:text-2xl lg:text-3xl text-neut-prim">
              {product?.name}
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 ml-11">
            {t('order_builder.description')}
          </p>
        </div>
      </div>
      {sourcing && (
        <div
          className="sticky top-0 z-10 bg-success-50 border border-success-200 rounded-lg px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm text-success-800"
          data-testid="sourcing-banner"
        >
          <span>
            {(() => {
              const destination = sourcingDestinationLabel(sourcing.order);
              return destination
                ? t('order_builder.sourcing.banner', { destination })
                : t('order_builder.sourcing.banner_generic');
            })()}
          </span>
          <span className="font-semibold text-success-700">
            {t('order_builder.sourcing.sub_orders_in_progress', {
              count: sourcing.order.subOrders.length,
            })}
          </span>
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-6">
          {sourcing && (
            <SourcingOrderItems
              order={sourcing.order}
              carts={carts}
              activeLengthId={sourcing.activeLengthId}
              onSelectItem={sourcing.onSelectItem}
            />
          )}
          <ProductDetails
            listingCount={listingCount}
            sellersCount={sellersCount}
            platformProduct={product}
            marketPrice={formattedMarketMidPrice}
            selectedLength={selectedPpl ?? null}
            capacityUnit={preferredUnit}
            vehicleLabel={vehicleLabel}
            transportVehicleId={currentTransportVehicleId}
          />
          <PricesAndAvailability
            preferredUnit={preferredUnit}
            onLengthsChange={setSelectedLengthIds}
            onModeChange={setMode}
            mode={mode}
            selectedLengthIds={selectedLengthIds}
            effectiveLengthIds={effectiveLengthIds}
            singleSelectedLengthId={singleLengthId}
            includeInventoryLengths={revealInventoryLengths}
            onIncludeInventoryLengthsChange={
              handleIncludeInventoryLengthsChange
            }
            platformProductLengths={productLengths as PlatformProductLength[]}
            orderInfo={orderInfo}
            carts={carts}
            sellerInventories={sellerInventories}
            hasMoreData={hasMoreSellerInventories}
            onSellerAdded={handleSellerAdded}
            platformProduct={product}
            onAddedToCart={handleAddedToCart}
            transportVehicleId={currentTransportVehicleId}
            selectedLocationId={locationId}
            selectedTallyName={selectedTallyName}
            parentOrderId={sourcing?.parentOrderId}
            vendorGroups={sellerVendorGroupsAccumulated}
            isVendorGroupsLoading={isSellerVendorGroupsLoading}
            isLoadingMoreVendorGroups={isSellerVendorGroupsLoadingMore}
            hasMoreVendorGroups={hasMoreSellerVendorGroups}
            vendorGroupsSentinelRef={sellerVendorGroupsSentinelRef}
            cannedTallyGroups={cannedTallyGroups}
            isCannedTallyGroupsLoading={isCannedTallySellerInventoriesLoading}
            isLoadingMoreCannedTallyGroups={isLoadingMoreSellerInventories}
            hasMoreCannedTallyGroups={hasMoreSellerInventories}
            cannedTallyGroupsSentinelRef={sellerInventoriesSentinelRef}
            sort={isCannedTally ? sellerInventorySort : sellerVendorGroupSort}
            onSort={
              isCannedTally
                ? handleSellerInventorySort
                : handleSellerVendorGroupSort
            }
            comparisonViews={comparisonViews}
            comparisonFiltersInput={comparisonFiltersInput}
          />
        </div>
        <div className="w-full lg:w-96 shrink-0 space-y-6">
          {sourcing ? (
            <SourcingCoverageGuide order={sourcing.order} carts={carts} />
          ) : (
            <InventoryRequirement
              selectedLengthCount={effectiveLengthIds.length}
              isCannedTally={isCannedTally}
              inventory={
                inventory?.buyerInventoryByProductAndLocation as Inventory
              }
              loading={isInventoryFetching}
              platformProductId={platformProductId}
            />
          )}
          <OrderCalculator
            onOrderInfoChange={handleOrderInfoChange}
            onUserTargetChange={() => {
              targetOrderSizeEditedRef.current = true;
            }}
            onTargetOrderSizeBlur={handleTargetOrderSizeBlur}
            onSuggestedWeeksToCover={(weeks) => {
              if (!targetOrderSizeEditedRef.current) {
                handleOrderInfoChange('targetWeeksToCover', weeks.toString());
              }
            }}
            orderInfo={orderInfo}
            averageNPrice={averageNPrice}
            nCount={nCount}
            formattedMarketMidPriceWithUnit={formattedMarketMidPrice}
            carts={carts}
            platformProductUnit={preferredUnit}
            currency={rawCurrency}
            isCannedTally={isCannedTally}
            platformProductLengths={
              effectiveLengthIds.length
                ? (productLengths as PlatformProductLength[]).filter((ppl) =>
                    effectiveLengthIds.includes(ppl.id)
                  )
                : (productLengths as PlatformProductLength[])
            }
            platformProductId={platformProductId}
            transportMode={transportMode}
            onTransportModeChange={setTransportMode}
            onTallyNameChange={setSelectedTallyName}
            transportVehicleCatalog={transportVehiclesData?.transportVehicles}
          />
        </div>
      </div>
    </div>
  );
}

export default function OrderBuilder() {
  const { t } = useTranslation('order-builder');
  const [searchParams] = useSearchParams();
  const platformProductLengthId = searchParams.get('platformProductLengthId');
  const platformProductId = searchParams.get('platformProductId');
  const returnPath = searchParams.get('returnPath');
  const parentOrderId = searchParams.get('parentOrderId');
  const dropShipLocationId = searchParams.get('dropShipLocationId');
  const isSourcingMode = Boolean(parentOrderId);

  const {
    data: parentOrderData,
    isLoading: isParentOrderLoading,
    isError: isParentOrderError,
    error: parentOrderError,
  } = useGetOrderById(parentOrderId ?? '', { enabled: isSourcingMode });
  const parentOrder = parentOrderData?.order;

  const effectiveDropShipLocationId =
    dropShipLocationId ?? parentOrder?.shippingLocation?.id ?? null;

  const [activeItem, setActiveItem] = useActiveSourcingItem(
    isSourcingMode,
    parentOrder,
    parentOrderId
  );

  const sourcingContext = useMemo<SourcingContext | undefined>(() => {
    if (!isSourcingMode || !parentOrder || !activeItem) return undefined;
    return {
      parentOrderId: parentOrderId as string,
      dropShipLocationId: effectiveDropShipLocationId,
      order: parentOrder,
      activeLengthId: activeItem.lengthId,
      onSelectItem: setActiveItem,
    };
  }, [
    isSourcingMode,
    parentOrder,
    activeItem,
    parentOrderId,
    effectiveDropShipLocationId,
    setActiveItem,
  ]);

  if (!returnPath) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1>Missing required parameters</h1>
      </div>
    );
  }

  if (isSourcingMode) {
    if (isParentOrderLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <PageLoadingSpinner name={t('order_builder.loading')} />
        </div>
      );
    }

    if (isParentOrderError || !parentOrder) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <h1>
            {t('order_builder.sourcing.load_error', {
              message: parentOrderError?.message ?? '',
            })}
          </h1>
        </div>
      );
    }

    if (!activeItem) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <h1>{t('order_builder.sourcing.no_items')}</h1>
        </div>
      );
    }

    return (
      <OrderBuilderPage
        platformProductId={activeItem.productId}
        platformProductLengthId={activeItem.lengthId}
        returnPath={returnPath}
        sourcing={sourcingContext}
      />
    );
  }

  if (!platformProductId) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1>Missing required parameters</h1>
      </div>
    );
  }

  return (
    <OrderBuilderPage
      platformProductId={platformProductId}
      platformProductLengthId={platformProductLengthId}
      returnPath={returnPath}
    />
  );
}
